import React, { useState } from 'react';
import { Link, useLocation, Outlet } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { useVisibility } from '../contexts/VisibilityContext';
import { Button } from './ui/button';
import {
  LayoutDashboard,
  Users,
  Car,
  Fuel,
  Building,
  FileText,
  Settings,
  LogOut,
  Menu,
  X,
  UserCircle,
  ClipboardList,
  Shield,
  Moon,
  Sun,
  Smartphone,
  Upload,
  MapPin,
  UserSearch,
  Eye,
  EyeOff,
  RefreshCw,
  Tractor
} from 'lucide-react';

export const Layout = () => {
  const location = useLocation();
  const { user, logout, isAdmin, isSupervisor, isSecretaria } = useAuth();
  const { isDark, toggleTheme } = useTheme();
  const { valuesVisible, toggleVisibility } = useVisibility();
  const [refreshing, setRefreshing] = useState(false);

  const handleRefresh = () => {
    setRefreshing(true);
    window.location.reload();
  };
  const [sidebarOpen, setSidebarOpen] = useState(false);

  // Build navigation based on role
  const navigation = [];

  // Admin tem acesso total
  if (isAdmin()) {
    navigation.push(
      { name: 'Dashboard', href: '/dashboard', icon: LayoutDashboard },
      { name: 'Abastecimentos', href: '/abastecimentos', icon: ClipboardList, badge: 'ADM' },
      { name: 'Importacao', href: '/importacao', icon: Upload, badge: 'ADM' },
      { name: 'Postos', href: '/postos', icon: Building },
      { name: 'Secretarias', href: '/secretarias', icon: Building },
      { name: 'Relatorios', href: '/relatorios', icon: FileText },
      { name: 'Filtro Individual', href: '/filtro-individual', icon: UserSearch },
      { name: 'Gerar Requisicao', href: '/gerar-requisicao', icon: ClipboardList },
      { name: 'Maquinas', href: '/maquinas', icon: Tractor },
      { name: 'Usuarios', href: '/usuarios', icon: Shield, badge: 'ADM' },
      { name: 'Configuracoes', href: '/configuracoes', icon: Settings, badge: 'ADM' }
    );
  }
  // Supervisor pode visualizar tudo (sem editar)
  else if (isSupervisor()) {
    navigation.push(
      { name: 'Dashboard', href: '/dashboard', icon: LayoutDashboard },
      { name: 'Postos', href: '/postos', icon: Building },
      { name: 'Secretarias', href: '/secretarias', icon: Building },
      { name: 'Relatorios', href: '/relatorios', icon: FileText },
      { name: 'Filtro Individual', href: '/filtro-individual', icon: UserSearch },
      { name: 'Gerar Requisicao', href: '/gerar-requisicao', icon: ClipboardList },
      { name: 'Maquinas', href: '/maquinas', icon: Tractor }
    );
  }
  // NOVO: Perfil Secretaria - APENAS Secretarias (sem Dashboard)
  else if (isSecretaria()) {
    navigation.push(
      { name: 'Secretarias', href: '/secretarias', icon: Building }
    );
  }
  // Usuario comum - APENAS Dashboard
  else {
    navigation.push(
      { name: 'Dashboard', href: '/dashboard', icon: LayoutDashboard }
    );
  }

  const getProfileLabel = () => {
    if (user?.profile === 'admin') return 'Administrador';
    if (user?.profile === 'supervisor') return 'Supervisor';
    if (user?.profile === 'secretaria') return 'Secretaria';
    return 'Usuário';
  };

  const getProfileColor = () => {
    if (user?.profile === 'admin') return 'bg-red-500';
    if (user?.profile === 'supervisor') return 'bg-yellow-500';
    if (user?.profile === 'secretaria') return 'bg-purple-500';
    return 'bg-green-500';
  };

  return (
    <div className="h-screen flex overflow-hidden">
      {/* Sidebar - ALWAYS fixed */}
      <aside className={`fixed inset-y-0 left-0 z-50 w-64 bg-[#003366] transform transition-transform duration-300 ease-in-out ${
        sidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'
      }`}>
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className="p-6 border-b border-white/10">
            <img src="/logo.png" alt="Prefeitura de Carauari" className="h-16 w-auto" />
            <h2 className="text-white text-sm font-semibold mt-3" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Controle de Abastecimento
            </h2>
          </div>

          {/* Navigation */}
          <nav className="flex-1 px-3 py-6 space-y-1 overflow-y-auto">
            {navigation.map((item) => {
              const Icon = item.icon;
              const isActive = location.pathname === item.href;
              return (
                <Link
                  key={item.name}
                  to={item.href}
                  data-testid={`nav-${item.name.toLowerCase().replace(/í/g, 'i').replace(/á/g, 'a').replace(/ã/g, 'a').replace(/ç/g, 'c').replace(/õ/g, 'o')}`}
                  className={`sidebar-nav-item flex items-center gap-3 px-3 py-3 rounded-md text-sm font-medium ${
                    isActive
                      ? 'active text-white'
                      : 'text-white/80 hover:text-white'
                  }`}
                  onClick={() => setSidebarOpen(false)}
                >
                  <Icon size={20} />
                  {item.name}
                  {item.badge && (
                    <span className="ml-auto text-xs bg-red-500 text-white px-1.5 py-0.5 rounded">{item.badge}</span>
                  )}
                </Link>
              );
            })}
          </nav>

          {/* User Info & Logout */}
          <div className="p-4 border-t border-white/10">
            <div className="flex items-center gap-3 mb-3 px-2">
              <UserCircle className="text-white" size={24} />
              <div className="flex-1 min-w-0">
                <p className="text-white text-sm font-medium truncate">{user?.name}</p>
                <div className="flex items-center gap-2">
                  <span className={`w-2 h-2 rounded-full ${getProfileColor()}`}></span>
                  <p className="text-white/60 text-xs">{getProfileLabel()}</p>
                </div>
              </div>
            </div>
            <Button
              onClick={logout}
              data-testid="logout-button"
              className="w-full bg-[#CC0000] hover:bg-[#990000] text-white"
              size="sm"
            >
              <LogOut size={16} className="mr-2" />
              Sair
            </Button>
          </div>
        </div>
      </aside>

      {/* Overlay for mobile */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Main Content - offset for fixed sidebar */}
      <div className="flex-1 flex flex-col min-w-0 lg:ml-64">
        {/* Top Bar */}
        <header className="glassmorphism sticky top-0 z-30 px-6 py-4 shadow-sm dark:bg-slate-800/90">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button
                onClick={() => setSidebarOpen(!sidebarOpen)}
                className="lg:hidden text-slate-700 dark:text-slate-200 hover:text-[#003366]"
                data-testid="mobile-menu-button"
              >
                {sidebarOpen ? <X size={24} /> : <Menu size={24} />}
              </button>
              <h1 className="text-2xl font-bold text-[#003366] dark:text-blue-400" style={{ fontFamily: 'Manrope, sans-serif' }}>
                PREFEITURA DE CARAUARI
              </h1>
            </div>
            <div className="flex items-center gap-3">
              {/* Refresh Button */}
              <button
                onClick={handleRefresh}
                className="p-2 rounded-lg bg-slate-100 dark:bg-slate-700 hover:bg-slate-200 dark:hover:bg-slate-600 transition-colors"
                data-testid="refresh-button"
                title="Atualizar Dados"
              >
                <RefreshCw size={20} className={`text-slate-600 dark:text-slate-300 ${refreshing ? 'animate-spin' : ''}`} />
              </button>
              {/* Visibility Toggle */}
              <button
                onClick={toggleVisibility}
                className="p-2 rounded-lg bg-slate-100 dark:bg-slate-700 hover:bg-slate-200 dark:hover:bg-slate-600 transition-colors"
                data-testid="visibility-toggle"
                title={valuesVisible ? 'Ocultar Valores' : 'Mostrar Valores'}
              >
                {valuesVisible ? <Eye size={20} className="text-slate-600 dark:text-slate-300" /> : <EyeOff size={20} className="text-red-500" />}
              </button>
              {/* Theme Toggle */}
              <button
                onClick={toggleTheme}
                className="p-2 rounded-lg bg-slate-100 dark:bg-slate-700 hover:bg-slate-200 dark:hover:bg-slate-600 transition-colors"
                data-testid="theme-toggle"
                title={isDark ? 'Modo Claro' : 'Modo Escuro'}
              >
                {isDark ? <Sun size={20} className="text-yellow-500" /> : <Moon size={20} className="text-slate-600" />}
              </button>
              {/* PWA Install hint (mobile) */}
              <div className="hidden sm:flex items-center gap-1 text-xs text-slate-500 dark:text-slate-400">
                <Smartphone size={14} />
                <span>PWA</span>
              </div>
              {/* Profile Badge */}
              <div className={`hidden md:flex items-center gap-2 px-3 py-1 rounded-full ${
                user?.profile === 'admin' ? 'bg-red-100 dark:bg-red-900/50 text-red-800 dark:text-red-300' : 
                user?.profile === 'supervisor' ? 'bg-yellow-100 dark:bg-yellow-900/50 text-yellow-800 dark:text-yellow-300' : 
                'bg-green-100 dark:bg-green-900/50 text-green-800 dark:text-green-300'
              }`}>
                <Shield size={16} />
                <span className="text-sm font-medium">{getProfileLabel()}</span>
              </div>
            </div>
          </div>
        </header>

        {/* Page Content - scrollable area */}
        <main className="flex-1 p-6 bg-[#F8FAFC] dark:bg-slate-900 overflow-y-auto">
          <Outlet />
        </main>
      </div>
    </div>
  );
};